import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthContext";
import axios from "axios";
import { getApiUrl } from "../../../config/api";

import ChannelsTab from "./tabs/ApiKeysTab/ChannelsTab";
import DocumentationTab from "./tabs/DocumentationTab/DocumentationTab";

// ── i18n ──────────────────────────────────────────────────────────────────────
const DICT = {
    pageTitle: { en: "API Management", zh: "API 管理" },
    pageSubtitle: {
        en: "Create and manage API channels for your product integrations",
        zh: "建立和管理產品整合的 API 頻道",
    },
};
const t = (key, lang) => DICT[key]?.[lang] || DICT[key]?.en || key;

// ── Tab config ────────────────────────────────────────────────────────────────
const TAB_CONFIG = {
    channels: {
        iconPath:
            "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z",
        labelEn: "API Keys",
        labelZh: "API 金鑰",
    },
    documentation: {
        iconPath:
            "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
        labelEn: "Documentation",
        labelZh: "文件說明",
    },
};

// ── Tab Button ────────────────────────────────────────────────────────────────
function TabButton({ tabKey, config, language, activeTab, onTabChange }) {
    const isActive = activeTab === tabKey;
    return (
        <button
            onClick={() => onTabChange(tabKey)}
            className={`relative flex flex-col md:flex-row items-center justify-center gap-1 sm:gap-2 md:gap-3 px-2 sm:px-3 md:px-4 lg:px-6 py-2 md:py-3 rounded-xl transition-all duration-200 flex-1 min-w-0 ${
                isActive
                    ? "bg-orange-500 text-white"
                    : "text-gray-600 hover:bg-slate-300"
            }`}
        >
            <svg
                className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={config.iconPath}
                />
            </svg>
            <span className="font-medium text-xs sm:text-sm truncate">
                {language === "zh" ? config.labelZh : config.labelEn}
            </span>
        </button>
    );
}

// ── ApiPage ───────────────────────────────────────────────────────────────────
function ApiPage() {
    const { currentUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const getLangFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return params.get("lang") === "zh" ? "zh" : "en";
    }, [location.search]);

    const [language, setLanguage] = useState(getLangFromUrl);
    const [activeTab, setActiveTab] = useState("channels");

    // Sync language ↔ URL
    useEffect(() => {
        setLanguage(getLangFromUrl());
    }, [getLangFromUrl]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("lang") !== language) {
            params.set("lang", language);
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [language, location.search, navigate]);

    // Guard: redirect if not logged in
    useEffect(() => {
        if (!currentUser) {
            navigate({ pathname: "/login", search: `?lang=${language}` });
        }
    }, [currentUser, navigate, language]);

    const [apiKeys, setApiKeys] = useState([]);
    const [fetchError, setFetchError] = useState(null);

    const fetchApiKey = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await axios.get(`${getApiUrl()}/api/v2/channels/`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (res.data.success) {
                setApiKeys(res.data.data || []);
            } else {
                setFetchError(res.data.message || "Failed to fetch API keys.");
            }
        } catch (err) {
            console.error("Error fetching API keys:", err);
            setFetchError(err.message || "Failed to fetch API keys.");
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        if (currentUser) fetchApiKey();
    }, [currentUser, fetchApiKey]);

    return (
        <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg font-sans overflow-hidden">
            <Sidebar />
            <LanguageSwitcher language={language} setLanguage={setLanguage} />

            <div className="flex-1 flex flex-col overflow-hidden pt-12">
                <div className="flex-1 overflow-y-auto">
                    <div className="w-5/6 mx-auto px-4 lg:px-8 py-8">
                        {/* ── Page Title ──────────────────────────────────────── */}
                        <div className="relative mb-8 lg:mb-12 text-center">
                            <h1 className="text-3xl lg:text-4xl font-bold text-black dark:text-white mb-2">
                                {t("pageTitle", language)}
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                                {t("pageSubtitle", language)}
                            </p>
                        </div>

                        {/* ── Tab Navigation ──────────────────────────────────── */}
                        <div className="flex justify-center mb-8 w-full">
                            <div className="inline-flex gap-1 sm:gap-2 p-1 sm:p-1.5 border border-gray-300/50 bg-gray-100 shadow-xl rounded-xl w-full overflow-x-auto">
                                {Object.entries(TAB_CONFIG).map(
                                    ([key, config]) => (
                                        <TabButton
                                            key={key}
                                            tabKey={key}
                                            config={config}
                                            language={language}
                                            activeTab={activeTab}
                                            onTabChange={setActiveTab}
                                        />
                                    ),
                                )}
                            </div>
                        </div>

                        {/* ── Tab Content ─────────────────────────────────────── */}
                        <div className="animate-fadeIn">
                            {activeTab === "channels" && (
                                <ChannelsTab
                                    language={language}
                                    currentUser={currentUser}
                                    apiKeys={apiKeys}
                                    loading={loading}
                                    fetchError={fetchError}
                                    onRetry={fetchApiKey}
                                />
                            )}
                            {activeTab === "documentation" && (
                                <DocumentationTab language={language} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ApiPage;
